import React from "react";
import { Popover, Backdrop } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import DateWiseDropdown from "./Filters/TimeRangeDropdown";
import PlatformDropdown from "./Filters/PlatformsDropdown";
import StatusDropdown from "./Filters/StatusDropdown";
import SourceDropdown from "./Filters/SourceDropdown";

const FONT = "'Host Grotesk', sans-serif";

const FilterPopover = ({
  anchorEl,
  onClose,
  activeBtn,
  handleDateWiseAPI,
  selectedPlatform,
  handlePlatformClick,
  selectedStatus,
  handleStatusClick,
  setSelectedStatus,
  handleClearFilters,
  // Source props
  activeSource,
  handleSourceClick,
}) => {
  const isOpen = Boolean(anchorEl);

  return (
    <>
      {/* Blur backdrop */}
      <Backdrop
        open={isOpen}
        onClick={onClose}
        sx={{
          zIndex: 1299,
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          backgroundColor: "rgba(15, 23, 42, 0.15)",
          transition: "all 0.2s ease",
        }}
      />

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 1300 }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px",
              mt: "8px",
              background: "transparent",
              boxShadow: "none",
              overflow: "visible",
            },
          },
        }}
      >
        <div
          style={{
            width: 300,
            background: "#ffffff",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 8px 30px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.04)",
            overflow: "hidden",
            fontFamily: FONT,
            animation: "fp-enter 0.18s cubic-bezier(0.34, 1.56, 0.64, 1)",
            transformOrigin: "top right",
          }}
        >
          <style>{`
            @keyframes fp-enter {
              from { opacity: 0; transform: scale(0.93) translateY(-6px); }
              to   { opacity: 1; transform: scale(1)    translateY(0);    }
            }
          `}</style>

          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 16px 12px",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(102,126,234,0.35)",
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon
                  icon={faSliders}
                  style={{ fontSize: "12px", color: "#fff" }}
                />
              </div>
              <span
                style={{
                  fontFamily: FONT,
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#0f172a",
                  letterSpacing: "0.01em",
                }}
              >
                Filters
              </span>
            </div>

            <button
              onClick={() => {
                handleClearFilters();
                onClose();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 11px",
                background: "white",
                border: "1.5px solid #e2e8f0",
                borderRadius: "20px",
                fontFamily: FONT,
                fontSize: "11.5px",
                fontWeight: 600,
                color: "#64748b",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a5b4fc";
                e.currentTarget.style.color = "#6366f1";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(99,102,241,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.color = "#64748b";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FontAwesomeIcon
                icon={faRotateLeft}
                style={{ fontSize: "11px" }}
              />
              Reset
            </button>
          </div>

          {/* Divider */}
          <div
            style={{
              height: "1px",
              background:
                "linear-gradient(to right, transparent, #e2e8f0 20%, #e2e8f0 80%, transparent)",
            }}
          />

          {/* Body */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              padding: "10px 10px 14px",
            }}
          >
            {[
              {
                label: "Date",
                control: (
                  <DateWiseDropdown
                    activeBtn={activeBtn}
                    handleDateWiseAPI={(val) => {
                      handleDateWiseAPI(val);
                      onClose();
                    }}
                  />
                ),
              },
              {
                label: "Platform",
                control: (
                  <PlatformDropdown
                    selectedPlatform={selectedPlatform}
                    handlePlatformClick={(val) => {
                      handlePlatformClick(val);
                      onClose();
                    }}
                  />
                ),
              },
              {
                label: "Status",
                control: (
                  <StatusDropdown
                    selectedStatus={selectedStatus}
                    handleStatusClick={(val) => {
                      handleStatusClick(val);
                      onClose();
                    }}
                    setSelectedStatus={setSelectedStatus}
                  />
                ),
              },
              {
                label: "Source",
                control: (
                  <SourceDropdown
                    activeSource={activeSource}
                    handleSourceClick={(val) => {
                      handleSourceClick(val);
                      onClose();
                    }}
                  />
                ),
              },
            ].map(({ label, control }) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 8px",
                  borderRadius: "10px",
                  transition: "background 0.12s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f8fafc")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#64748b",
                    flexShrink: 0,
                    width: 60,
                  }}
                >
                  {label}
                </span>
                <div style={{ width: "175px" }}>{control}</div>
              </div>
            ))}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default FilterPopover;